<template>
  <Modal
    :backgroundColor="gradientLight2"
    :bodyClass="$style.modalBody"
    :cancelText="getCancelText"
    :compressed="compressed"
    :enableMainAction="enableMainAction && !getTableImportConditions().disableImportUsers"
    :handleMainAction="handleMainAction"
    :headerIcon="headerIcon"
    :headerSubtext="getHeaderSubtext"
    :headerText="headerText"
    :isUserImportModal="true"
    :loading="loading"
    :mainActionText="mainActionText"
    :subTextUnderTitle="subTextUnderTitle"
    :useHeaderIconStyleClass="useHeaderIconStyleClass"
    :visible="visible"
    width="65%"
    @closed="handleModalClosed"
    @updateVisibility="handleUpdateVisibilityChanged"
  >
    <transition
      :enterActiveClass="$style.fadeEnterActive"
      :enterClass="$style.fadeTo"
      :leaveActiveClass="$style.fadeLeaveActive"
      :leaveToClass="$style.fadeTo"
    >
      <ImportUsersPaywallMessage
        v-if="showPaywallMessage()"
      />
      <UserCapBanner
        v-else-if="isProviderOrg"
        :totalCountOfAllItems="totalSystemUsers"
      />
    </transition>
    <template
      v-if="showDisclaimer"
      #disclaimer
    >
      <p
        :class="getDisclaimerClass"
      >
        {{ getDisclaimerText }}
      </p>
    </template>
    <div :class="$style.container">
      <div :class="contentSelectionClassName">
        <ImportError
          v-if="importError"
          :error="importError"
          :importType="importType"
        />
        <slot
          v-else
          :handleMainAction="handleMainAction"
          name="selectionComponent"
          :tableImportConditions="getTableImportConditions()"
        />
      </div>
      <div :class="contentResultsClassName">
        <ImportUsersResults
          :importType="importType"
          :itemType="itemType"
          :resultsJobId="resultsJobId"
        />
      </div>
    </div>
    <template v-slot:footerComponentSlot>
      <div :class="$style.footerInfoContainer">
        <ImportUserCurrentCount v-if="showCounters" />
        <slot
          v-if="showCounters"
          name="toBeImportedUsersCounter"
        />
      </div>
    </template>
  </Modal>
</template>
<script>
import { get } from 'lodash';
import { gradientLight2 } from 'client/css/colors.css';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util/index';
import Constants, {
  ImportUsersConsts,
  ItemTypeConsts,
  Sizes,
  StoreModuleMap,
} from 'client/util/Constants';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ImportError from 'client/components/ImportUsers/ImportError.vue';
import ImportUserCurrentCount from 'client/components/ImportUsers/ImportUserCurrentCount.vue';
import ImportUsersPaywallMessage from 'client/components/ImportUsers/ImportUsersPaywallMessage';
import ImportUsersResults from 'client/components/ImportUsers/ImportUsersResults.vue';
import JobUtils from 'client/util/JobUtils';
import Modal from 'client/components/Modal/Modal.vue';
import UserCapBanner from 'client/Payments/components/UserCapBanner';

const { error } = Constants;
const { defaultMaxUsersDisclaimer } = ImportUsersConsts;
const { selection, results } = ImportUsersConsts.views;
const organizationsCollection = StoreModuleMap[ItemTypeConsts.organizations];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const systemUserCollection = StoreModuleMap[ItemTypeConsts.systemUser];
const resultsHeaderSubtext = 'Your import was quite large. We will email the results of your import when it’s completed.';

/**
 * The ImportUsersModal uses the components and Vuex modules originally built for the Workday bulk
 * import to import system users from an external data source. External data sources could include
 * an external application such as Workday, Google Workspace, or Slack that expose API endpoints
 * that return arrays of users, or a CSV file. The remote data source should be encapsulated in a
 * Vuex module that uses the ImportUsersMixin.
 */
export default {
  name: 'ImportUsersModal',

  components: {
    ImportError,
    ImportUserCurrentCount,
    ImportUsersPaywallMessage,
    ImportUsersResults,
    Modal,
    UserCapBanner,
  },

  props: {
    cancelText: {
      default: 'cancel',
      type: String,
    },

    disclaimerText: {
      default: defaultMaxUsersDisclaimer,
      type: String,
    },

    handleCancel: {
      default: () => {},
      type: Function,
    },

    handleMainActionSuccess: {
      default() {
        this.downloadCsv();
      },
      type: Function,
    },

    handleViewChange: {
      default: () => {},
      type: Function,
    },

    headerIcon: {
      type: Object,
      default: null,
    },

    headerSubtext: {
      type: [String, Object],
      default: '',
    },

    headerText: {
      type: String,
      default: 'Import Users',
    },

    hideCloseAction: {
      default: false,
      type: Boolean,
    },

    hideResultsMainAction: {
      default: false,
      type: Boolean,
    },
    hideSelectionMainAction: {
      default: false,
      type: Boolean,
    },
    // this is used for Segment event tacking
    importType: {
      type: String,
      required: true,
    },

    isUserImportModal: {
      type: Boolean,
      default: false,
    },

    itemType: {
      type: String,
      required: true,
      // TODO: Add ItemTypeConsts custom validator
    },

    resultsDisclaimerText: {
      default: '',
      type: String,
    },

    resultsJobId: {
      type: String,
      default: '',
    },

    resultsMainActionText: {
      default: 'download CSV',
      type: String,
    },

    showResultsView: {
      type: Boolean,
      default: false,
    },

    subTextUnderTitle: {
      default: false,
      type: Boolean,
    },

    useHeaderIconStyleClass: {
      type: Boolean,
      default: true,
    },

    visible: {
      default: false,
      type: Boolean,
    },
  },

  data() {
    return {
      activeContent: selection,
      closeText: false,
      compressed: false,
      enableMainAction: false,
      gradientLight2,
      loading: false,
      showResultsHeaderSubtext: false,
      sizes: {
        ...Sizes,
      },
    };
  },

  computed: {
    ...mapGetters(settingsModule, ['isProviderOrg']),

    ...mapGetters(organizationsCollection, ['getItemDetails']),

    ...mapGetters(systemUserCollection, {
      totalSystemUsers: 'getTotalCountOfAllItems',
    }),

    ...mapState(settingsModule, [
      'isCustomer',
      'maxSystemUsers',
      'orgId',
    ]),

    contentResultsClassName() {
      if (this.activeContent === results) {
        return this.$style.contentResultsActive;
      }

      return this.$style.contentResults;
    },

    contentSelectionClassName() {
      if (this.activeContent === selection) {
        return this.$style.contentSelectionActive;
      }

      return this.$style.contentSelection;
    },

    getCancelText() {
      if (this.hideCloseAction) {
        return '';
      }

      if (this.closeText) {
        return 'close';
      }
      return this.cancelText;
    },

    getDisclaimerClass() {
      return this.$style.userLimitDisclaimer;
    },

    getDisclaimerText() {
      let outputDisclaimerText = defaultMaxUsersDisclaimer;
      if (this.isCustomer) {
        const inSelectionView = this.activeContent === selection;
        const noResultsDisclaimerProvided = this.resultsDisclaimerText === '';
        const useDefaultDisclaimer = inSelectionView || noResultsDisclaimerProvided;

        outputDisclaimerText = useDefaultDisclaimer
          ? this.disclaimerText
          : this.resultsDisclaimerText;
      }

      return outputDisclaimerText;
    },

    /**
     * Computed property to render a different subheader text
     * when on results view in the import users modal
     */
    getHeaderSubtext() {
      if (typeof this.headerSubtext === 'string') return this.headerSubtext;
      const texts = this.headerSubtext;
      let textToShow;
      // Indicates that we are on the import results pane
      if (this.showResultsHeaderSubtext) {
        // Indicate wether the import was bulk or not (> 100)
        textToShow = this.isBulkImport
          ? texts.bulkResultsHeaderSubtext : texts.resultsHeaderSubtext;
      } else {
        // Use selection view subheader
        textToShow = texts.selectionHeaderSubtext;
      }
      return textToShow;
    },

    headerSubtextString() {
      return this.activeContent === selection ? this.headerSubtext : resultsHeaderSubtext;
    },

    mainActionText() {
      let mainActionText;
      switch (this.activeContent) {
        case selection:
          mainActionText = this.getSelectionMainActionText();
          break;
        case results:
          mainActionText = this.getResultsMainActionText();
          break;
        default:
          mainActionText = '';
          break;
      }
      return mainActionText;
    },

    showCounters() {
      return this.activeContent === selection;
    },

    showDisclaimer() {
      // Disclaimer text is about non-customer user limit
      const isUserLimitDisclaimer = (this.getDisclaimerText === defaultMaxUsersDisclaimer);
      return !(this.isCustomer && isUserLimitDisclaimer);
    },

  },

  watch: {
    selectedItemsCount(newCount) {
      if (this.activeContent === selection) {
        if (newCount === 0) {
          this.enableMainAction = false;
        } else {
          this.enableMainAction = true;
        }
      }
    },

    validationErrorCount(newCount) {
      if (newCount > 0) {
        this.enableMainAction = false;
      } else if (this.selectedItemsCount > 0) {
        this.enableMainAction = true;
      }
    },

    visible() {
      /**
       * Disable the main action button when hidden so that it will be disabled
       * if the modal is re-opened.
      */
      if (!this.visible) {
        this.enableMainAction = false;
      }
    },
  },

  created() {
    if (this.showResultsView) {
      this.activeContent = results;
    }
  },
  /**
   * Extend the computed properties and methods here, before the component is
   * created so that the namespace argument to mapActions() and mapGetters()
   * can be set dynamically from the props passed to the ItemTable component.
   * @see https://github.com/vuejs/vuex/issues/863#issuecomment-315619012
   */
  beforeCreate() {
    const storeModule = StoreModuleMap[this.$options.propsData.itemType];

    this.$options.computed = {
      ...this.$options.computed,
      ...mapGetters(storeModule, {
        importError: 'getImportError',
        importedUsers: 'getImportedUsers',
        isBulkImport: 'getIsBulkImport',
        isImportComplete: 'getIsImportComplete',
        jobId: 'getJobId',
        selectedItemsCount: 'getSelectedItemsCount',
        totalCountOfAllItems: 'getTotalCountOfAllItems',
        validationErrorCount: 'getValidationErrorCount',
      }),
    };

    this.$options.methods = {
      ...this.$options.methods,
      ...mapActions(storeModule, [
        'importSelectedUsers',
        'setIsImportComplete',
        'unsetImportError',
      ]),
      ...mapActions(systemUserCollection, ['updateModifiedItemCount']),
    };

    this.$store.subscribe((mutation) => {
      if (mutation.type.includes('SET_IS_IMPORT_COMPLETE')) {
        this.handleIsImportComplete(mutation.payload);
      }
    });
  },

  methods: {
    ...mapActions(systemUserCollection, {
      refreshSystemUsers: 'refreshCollection',
    }),

    downloadCsv() {
      try {
        // Remove the id column since it isn't meaningful to the user
        const importList = this.importedUsers.map((user) => {
          const updatedUser = user;
          delete updatedUser.id;
          return updatedUser;
        });

        // Download the results of the CSV import
        JobUtils.downloadCsv(this.jobId, importList, 'bulk');
      } catch (err) {
        notify(error, 'There was an error downloading the CSV, please try again later.');
      }
    },

    getResultsMainActionText() {
      if (this.hideResultsMainAction) return '';

      // Setting the mainActionText to empty hides the footer button
      return !this.isBulkImport ? this.resultsMainActionText : '';
    },

    getSelectionMainActionText() {
      return this.hideSelectionMainAction ? '' : 'import';
    },

    getTableImportConditions() {
      const conditions = {
        disableImportUsers: false,
        disableSelectUsers: false,
        disableSelectAllUsers: false,
      };

      const { systemUsersCount } = this.getItemDetails(this.orgId);

      const noUserCapValue = 0;
      const isDirectOrgCustomer = !this.isProviderOrg && this.isCustomer;
      const isProviderOrgWithoutCap = this.isProviderOrg
        && this.maxSystemUsers === noUserCapValue;

      if (isDirectOrgCustomer || isProviderOrgWithoutCap) {
        return conditions;
      }

      const shouldDisableImport = (systemUsersCount >= this.maxSystemUsers);
      const shouldDisableSelectAll = (systemUsersCount + this.totalCountOfAllItems)
        >= this.maxSystemUsers;

      conditions.disableImportUsers = shouldDisableImport;
      conditions.disableSelectUsers = (systemUsersCount + this.selectedItemsCount)
        >= this.maxSystemUsers;
      conditions.disableSelectAllUsers = !this.isCustomer
        || shouldDisableImport
        || shouldDisableSelectAll;

      this.compressed = conditions.disableImportUsers;
      return conditions;
    },

    handleIsImportComplete(newValue) {
      this.enableMainAction = newValue;
      this.loading = false;
      if (newValue === true && !this.isBulkImport && this.importedUsers) {
        const userFilter = user => user.importStatus === 'finished';
        const successfullyImported = this.importedUsers.filter(userFilter).length;

        EventHelper.triggerImportUsersComplete({
          importType: this.importType,
          successfullyImported,
          usersToImport: this.importedUsers.length,
        });
      }
    },

    handleMainAction() {
      if (!this.loading) {
        if (this.activeContent === selection) {
          this.loading = true;
          this
            .importSelectedUsers()
            .then(() => {
              this.showResultsHeaderSubtext = true;
              this.activeContent = results;
              this.closeText = true;
              this.enableMainAction = this.isImportComplete;
              this.handleViewChange(this.activeContent);
            })
            .catch((err) => {
              this.loading = false;
              const statusCode = get(err, ['response', 'status']);
              const errorCodeMsg = statusCode ? `Status code ${statusCode}. ` : '';
              const message = `${errorCodeMsg}We were unable to import users. Please try again and contact our support team if the problem persists.`;
              notify(error, message);
            });
        } else {
          this.handleMainActionSuccess();
        }
      }
    },

    /**
     * After the modal has been hidden, reset data members so that if it
     * displayed again, it will show the selection view, not the results view.
     */
    handleModalClosed() {
      this.activeContent = selection;
      this.closeText = false;
      this.loading = false;
      this.setIsImportComplete(false);
    },

    handleUpdateVisibilityChanged(visibility) {
      if (visibility === false) {
        this.handleCancel();
        this.updateModifiedItemCount();
        this.refreshSystemUsers();
        this.unsetImportError();
      }
    },

    showPaywallMessage() {
      const { disableImportUsers, disableSelectUsers } = this.getTableImportConditions();
      return !this.isProviderOrg && (disableImportUsers || disableSelectUsers);
    },
  },
};
</script>
<style module>
@value (fadeTransition) from '~css/transitions.css';

.container {
  flex: 1;
  position: relative;
  width: 100%;
}

.content {
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  transition: transform 0.5s ease-in-out;
  width: 100%;
}

.contentSelection {
  composes: content;
  transform: translateX(-150%);
}

.contentSelectionActive {
  composes: content;
  transform: translateX(0);
}

.contentResults {
  composes: content;
  transform: translateX(150%);
}

.contentResultsActive {
  composes: content;
  transform: translateX(0);
}

.userLimitDisclaimer {
  font-size: 0.9rem;
  margin-left: 35px;
  margin-top: 10px;
  width: 75%;
}

.fadeTo,
.fadeLeaveTo {
  opacity: 0;
}

.fadeEnterActive,
.fadeEnter {
  transition: fadeTransition;
}

.footerInfoContainer {
  align-items: center;
  display: inline-flex;
  flex: auto;
}

.modalBody.modalBody {
  display: flex;
  flex-direction: column;
  height: calc(75vh - 100px);
  padding-top: 0;
}
</style>
